import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useDispatch} from "react-redux"
import {Container} from "../../components/Container"
import {backButtonToggle} from "../../store/ui/actions"
import {useLocalization} from "../../hooks/localization"
import {Row} from "../../components/Row"
import {ItemList} from "../../components/ItemList"
import {useKeyboard} from "../../hooks/keyboard"
import {SettingSelectComponent} from "../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../components/Select"
import {
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ID_BELL,
    CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON,
    CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN,
    CONTROL_TYPE_ID_SIREN,
    ControlTypeModel,
    SOUND_LEVEL_TYPE_ID,
    SOUND_PLAYED_TYPE_ID
} from "../../types/control"
import {useFilterSelector} from "../../hooks/filter-selector"
import {AreaModel} from "../../store/control/state"
import cancelIcon from "../../assets/images/icons/alarm/cancel.png"
import "./index.scss"
import {useSetSelector} from "../../hooks/set-selector"

export const RadioButton = memo(function RadioButton({
    isSelected = false,
    title,
    onClick
}: {
    isSelected: boolean
    title: string
    onClick: () => void
}) {
    return (
        <div className="radio-button-container" onClick={onClick}>
            <div className={`radio-pointer ${isSelected ? "radio-selected" : ""}`} />
            <span>{title}</span>
        </div>
    )
})

function exceptControls(query: string): boolean {
    if (
        query !== CONTROL_TYPE_ID_ALARM &&
        query !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON &&
        query !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN &&
        query !== CONTROL_TYPE_ID_SIREN &&
        query !== CONTROL_TYPE_ID_BELL &&
        query !== SOUND_LEVEL_TYPE_ID &&
        query !== SOUND_PLAYED_TYPE_ID
    ) {
        return true
    }
    return false
}

export const AllControlsScreen = memo(function AllControlsScreen() {
    const {t} = useLocalization()
    const [setActiveInput, renderKeyboard] = useKeyboard()
    const [search, setSearch] = useState<string>("")
    const dispatch = useDispatch()
    const searchRef = useRef<HTMLInputElement>()

    const controlList = useFilterSelector(
        state => state.control.controlList,
        control => exceptControls(control?.type_id),
        []
    )
    const controlTypeSetList = useSetSelector(state => state.control.controlTypeList)

    const controlTypeList = useMemo(() => {
        const list = []
        const typeIdTracker: {[key: string]: boolean} = {}
        for (const item of controlList) {
            if (!typeIdTracker[item?.type_id]) {
                typeIdTracker[item?.type_id] = true
                list.push(controlTypeSetList[item?.type_id])
            }
        }
        return list as ControlTypeModel[]
    }, [controlList, controlTypeSetList])

    const areaList = useFilterSelector(
        state => state.control.areaList,
        area => area.type === "room" || area.type === "place",
        []
    )
    const [openModal, setOpenModal] = useState(false)
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(null)
    const [controlType, setControlType] = useState<SelectItem<Omit<ControlTypeModel, "image_set">>>(null)

    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, "image_set">>[] = useMemo(
        () =>
            controlTypeList
                .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
                .map(controlType => ({
                    value: controlType,
                    label: controlType.name
                })),
        [controlTypeList]
    )

    const areaOptionList: SelectItem<AreaModel>[] = useMemo(
        () => areaList.map(area => ({value: area, label: area.name})),
        [areaList]
    )

    const onBlur = useCallback(() => setActiveInput(null), [setActiveInput])
    const onFocus = useCallback(() => setActiveInput([searchRef, search, setSearch]), [setActiveInput, search])

    const [defaultOrder, setDefaultOrder] = useState(true)
    const [alphabeticalOrder, setAlphabeticalOrder] = useState(false)
    const [groupOrder, setGroupOrder] = useState(false)

    const onClickDefaultOrder = useCallback(() => {
        setGroupOrder(false)
        setAlphabeticalOrder(false)
        setDefaultOrder(true)
    }, [])

    const onClickAlphabeticalOrder = useCallback(() => {
        setGroupOrder(false)
        setAlphabeticalOrder(true)
        setDefaultOrder(false)
    }, [])

    const onClickGroupOrder = useCallback(() => {
        setAlphabeticalOrder(false)
        setGroupOrder(true)
        setDefaultOrder(false)
    }, [])

    const [sortByName, setSortByName] = useState(false)
    const [sortByGroup, setSortByGroup] = useState(false)
    const [sortByDefault, setSortByDefault] = useState(true)

    const onClickSort = useCallback(() => {
        if (alphabeticalOrder) {
            setSortByGroup(false)
            setSortByDefault(false)
            setSortByName(p => !p)
        } else if (groupOrder) {
            setSortByName(false)
            setSortByDefault(false)
            setSortByGroup(p => !p)
        } else if (defaultOrder) {
            setSortByName(false)
            setSortByGroup(false)
            setSortByDefault(p => !p)
        }
    }, [alphabeticalOrder, defaultOrder, groupOrder])
    const [advancedFilteredList, setAdvancedFilteredList] = useState(false)

    const searchControl = useMemo(() => {
        const newValue = controlList.filter(value => {
            if (advancedFilteredList) {
                if (
                    areaOption?.value != null &&
                    controlType?.value == null &&
                    value?.area_id === areaOption?.value?.id
                ) {
                    return value
                } else if (controlType?.value == null && areaOption?.value == null) {
                    return value
                } else if (
                    controlType?.value != null &&
                    areaOption?.value == null &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    controlType?.value == null &&
                    value?.area_id === areaOption?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    controlType?.value != null &&
                    value?.area_id === areaOption?.value?.id &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value == null &&
                    controlType?.value != null &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    controlType?.value != null &&
                    value?.area_id === areaOption?.value?.id &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                }
                return null
            }
            return null
        })
        const t = advancedFilteredList ? [...newValue] : [...controlList]
        return t
            .filter(value => value.name.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) =>
                alphabeticalOrder
                    ? sortByName
                        ? a.name.localeCompare(b.name)
                            ? -1
                            : 1
                        : a.name.localeCompare(b.name)
                        ? 1
                        : -1
                    : groupOrder
                    ? sortByGroup
                        ? a.type_id > b.type_id
                            ? -1
                            : 1
                        : a.type_id > b.type_id
                        ? 1
                        : -1
                    : defaultOrder && sortByDefault
                    ? a?.parameters?.["order"] > b?.parameters?.["order"]
                        ? 1
                        : -1
                    : a?.parameters?.["order"] > b?.parameters?.["order"]
                    ? -1
                    : 1
            )
    }, [
        search,
        controlList,
        sortByName,
        defaultOrder,
        sortByDefault,
        sortByGroup,
        alphabeticalOrder,
        groupOrder,
        advancedFilteredList,
        areaOption?.value,
        controlType?.value
    ])

    const applyFilter = useCallback(() => setOpenModal(false), [])
    const checkFilter = useMemo(
        () =>
            defaultOrder &&
            sortByDefault &&
            !groupOrder &&
            !alphabeticalOrder &&
            !sortByGroup &&
            !sortByName &&
            search === "" &&
            search.length === 0 &&
            areaOption == null &&
            controlType == null,
        [
            defaultOrder,
            alphabeticalOrder,
            sortByGroup,
            sortByName,
            sortByDefault,
            search,
            areaOption,
            controlType,
            groupOrder
        ]
    )

    const onClickClearAllFilter = useCallback(() => {
        if (checkFilter) {
            return
        }
        setDefaultOrder(true)
        setSortByDefault(true)
        setGroupOrder(false)
        setSortByGroup(false)
        setAlphabeticalOrder(false)
        setSortByName(false)
        setAreaOption(null)
        setControlType(null)
        setAdvancedFilteredList(false)
        setSearch("")
    }, [checkFilter])

    useEffect(() => {
        if (areaOption?.value == null && controlType?.value == null) {
            setAdvancedFilteredList(false)
        } else {
            setAdvancedFilteredList(true)
        }
    }, [areaOption, controlType])

    //dispatch(setGroupControlsAction(controlList))
    dispatch(backButtonToggle({is_open: true, to: "/"}))
    return (
        <>
            <div className="functional-modal-backdrop" style={{display: openModal ? "flex" : "none"}}>
                <div className="fnl-modal-container">
                    <span>{t("advanced_filtering")}</span>
                    <div className="fnl-modal-options">
                        <div className="fnl-option-field">
                            <span>{t("area")}</span>
                            <div className="fnl-option">
                                <SettingSelectComponent
                                    label={""}
                                    options={areaOptionList}
                                    onChange={setAreaOption}
                                    value={areaOption}
                                />
                                <span onClick={() => setAreaOption(null)}>
                                    <img src={cancelIcon} alt="" />
                                </span>
                            </div>
                        </div>
                        <div className="fnl-option-field">
                            <span>{t("control")}</span>
                            <div className="fnl-option">
                                <SettingSelectComponent
                                    label={""}
                                    options={controlTypeOptionList}
                                    onChange={setControlType}
                                    value={controlType}
                                />
                                <span onClick={() => setControlType(null)}>
                                    <img src={cancelIcon} alt="" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="fnl-modal-actions">
                        <span onClick={applyFilter}>
                            <span>{t("ok")}</span>
                        </span>
                    </div>
                </div>
            </div>
            <Container>
                <div className="page-wrapper">
                    <div className="page-search-input-container">
                        <RadioButton title={t("default")} isSelected={defaultOrder} onClick={onClickDefaultOrder} />
                        <RadioButton title={t("group_sorting")} isSelected={groupOrder} onClick={onClickGroupOrder} />
                        <RadioButton
                            title={t("alphabetical_sorting")}
                            isSelected={alphabeticalOrder}
                            onClick={onClickAlphabeticalOrder}
                        />
                        <span onClick={onClickSort}>{t("change_ranking")}</span>
                        <span
                            onClick={() => setOpenModal(true)}
                            className={areaOption !== null || controlType !== null ? "bg-selected" : ""}
                        >
                            {t("advanced_filtering")}
                        </span>
                        <span onClick={onClickClearAllFilter} className={checkFilter ? "bg-not-allowed" : ""}>
                            {t("clear_filters")}
                        </span>
                    </div>
                    <div className="page-search-input-container">
                        <input
                            type="text"
                            maxLength={50}
                            placeholder={t("search_control")}
                            ref={searchRef}
                            onChange={e => setSearch(e.target.value)}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            value={search}
                        />
                        <div
                            className={`filter-clear-button ${
                                search.length === 0 || search === "" ? "bg-not-allowed" : ""
                            }`}
                            onClick={() => {
                                if (search.length === 0 || search === "") {
                                    return
                                }
                                setSearch("")
                            }}
                        >
                            {t("clear")}
                        </div>
                    </div>
                    <Row>
                        {renderIf(searchControl.length > 0)(
                            () => (
                                <ItemList controls={searchControl} />
                            ),
                            () => (
                                <span className="search-message-text">{t("search_not_found")}</span>
                            )
                        )}
                    </Row>
                </div>
            </Container>
            {renderKeyboard()}
        </>
    )
})
